






import { Component, Vue } from 'vue-property-decorator'
import Store from '@/api/models/store/Store'
import { Action, Getter, Mutation } from 'vuex-class'
import { RootStateMutations } from '@/store/mutations'
import CdekPickupPoint from '@/models/CdekPickupPoint'
import PickupPointsRequest from '@/api/models/request/PickupPointsRequest'
import Account from '@/api/models/Account'
import CdekCity from '@/api/models/CdekCity'
import StoreMap from '@/api/models/store/StoreMap'
import UpdateStoreMapRequest from '@/api/models/request/UpdateStoreMapRequest'
import { getStoreCodeHash, getStoreAddressHash } from '@/components/HashHelper'

@Component({})
export default class StoresBase extends Vue {
    $refs: {
        errorMessage: Element;
    }

    protected error = ''
    protected needOpenCitySelect: {[key: string]: boolean } = {}
    protected needOpenTerminalSelect: {[key: string]: boolean } = {}
    protected citiesByStore: {[key: string]: Array<CdekCity>} = {}
    protected systemStores: Array<Store> = []
    protected emptyPoints: {[key: string]: boolean} = {}
    // Ключом является хэш адреса склада, строящийся как: 'md5('Id acc-countryIso-region-city-citId')
    protected pickupPointsMap: {[key: string]: Array<CdekPickupPoint>} = {}
    protected blockStores: {[key: string]: boolean} = {}

    @Getter('currentAccount')
    protected currentAccount: Account

    @Mutation(RootStateMutations.setLoading)
    protected setLoading: (load: boolean) => void

    @Action('getStores')
    protected getStores: () => Promise<Array<Store>>

    @Action('getCdekCities')
    protected getCdekCities: (city: string) => Promise<Array<CdekCity>>

    @Action('updateWarehouseMapping')
    protected updateWarehouseMapping: (request: UpdateStoreMapRequest) => Promise<void>

    @Action('getCdekPickupPoints')
    protected getCdekPickupPoints: (req: PickupPointsRequest) => Promise<Array<CdekPickupPoint>>

    async created(): Promise<void> {
        await this.loadData()
    }

    async loadData(): Promise<void> {
        this.setLoading(true)
        try {
            this.systemStores = await this.getStores()
        } catch (err) {
            this.error = err.response?.data?.message ?? this.$t('settings.stores.errors.getData').toString()
        } finally {
            this.setLoading(false)
        }
    }

    protected async getCities(city: string, storeCode: string, selectedCity?: CdekCity): Promise<void> {
        this.clearError()

        try {
            const response = await this.getCdekCities(city)
            const obj = { [storeCode]: response.slice(0, 50) }
            this.citiesByStore = Object.assign({}, this.citiesByStore, obj)

            if (selectedCity) {
                this.injectCity(selectedCity, storeCode)
            }

            if (obj[storeCode].length > 0) {
                this.needOpenCitySelect = Object.assign({}, this.needOpenCitySelect, { [storeCode]: true })
            }
        } catch (err) {
            this.error = err.response?.data?.message ?? this.$t('settings.stores.errors.getData').toString()
        }
    }

    protected clearAllCities(storeCode: string): void {
        this.citiesByStore = Object.assign({}, this.citiesByStore, { [storeCode]: [] })
        this.needOpenCitySelect = Object.assign({}, this.needOpenCitySelect, { [storeCode]: false })
    }

    protected injectCity(city: CdekCity, storeCode: string): void {
        const isExistCity = this.citiesByStore[storeCode]?.find((cityItem) => cityItem.code === city.code)

        if (!isExistCity) {
            const cities = this.citiesByStore[storeCode] ?? []
            cities.push(city)

            this.citiesByStore = Object.assign(
                {},
                this.citiesByStore,
                { [storeCode]: cities }
            )
        }
    }

    protected async updateStoreMap(map: StoreMap, needUpdateModule: boolean): Promise<void> {
        this.clearError()

        if (!this.currentAccount.id) {
            return
        }

        this.setLoading(true)
        try {
            const request: UpdateStoreMapRequest = {
                accountId: this.currentAccount.id,
                map,
                needUpdateModule
            }
            await this.updateWarehouseMapping(request)
        } catch (err) {
            this.error = err.response?.data?.message ?? this.$t('settings.stores.errors.updateStoreMap').toString()
        } finally {
            this.setLoading(false)
        }
    }

    protected setBlockStores(): void {
        for (const store of this.systemStores) {
            this.blockStores = Object.assign({}, this.blockStores, { [store.code]: true })
        }
    }

    protected async getPointsHandler(store: Store, city?: CdekCity): Promise<void> {
        this.clearError()

        if (!this.isValidStoreAddress(store)) {
            this.emptyPoints = Object.assign(
                {},
                this.emptyPoints,
                { [store.code]: true }
            )

            return
        }

        const request: PickupPointsRequest = {
            accountId: Number(this.currentAccount.id),
            storeAddress: store.address,
            storeCode: store.code
        }

        try {
            this.setLoading(true)
            const response = await this.getCdekPickupPoints(request)

            if (!this.emptyPoints[store.code] && !(response.length > 0)) {
                this.emptyPoints = Object.assign(
                    {},
                    this.emptyPoints,
                    { [store.code]: true }
                )
            } else {
                if (undefined !== city) {
                    // записываем результат только по коду
                    const cityKey = getStoreCodeHash(this.currentAccount, city.code)
                    this.pickupPointsMap = Object.assign(
                        {},
                        this.pickupPointsMap,
                        { [cityKey]: response }
                    )
                } else {
                    const addressKey = getStoreAddressHash(this.currentAccount, store)
                    const cityKey = getStoreCodeHash(this.currentAccount, response[0].city.code)
                    this.pickupPointsMap = Object.assign(
                        {},
                        this.pickupPointsMap,
                        { [addressKey]: response },
                        { [cityKey]: response }
                    )
                }

                this.needOpenTerminalSelect = Object.assign(
                    {},
                    this.needOpenTerminalSelect,
                    { [store.code]: true }
                )
            }
        } catch (err) {
            this.error = err.response?.data?.message ?? this.$t('settings.stores.errors.getData').toString()
        } finally {
            this.setLoading(false)
        }
    }

    isValidStoreAddress(store: Store): boolean {
        return !!store.address?.cityId || !!store.address?.city
    }

    scrollToError(): void {
        this.$refs.errorMessage.scrollIntoView({ behavior: 'smooth' })
    }

    clearError(): void {
        this.error = ''
    }
}
